import { useEffect, useState } from 'react'
import { api, type SimulationRow } from '../lib/api.ts'
import { Button } from '../components/Button.tsx'
import { icons } from '../components/icons.ts'
import { forgetDraft, listDrafts, type StoredDraft } from '../lib/draftStore.ts'
import { STATUS_LABEL, label } from '../lib/vocabulary.ts'
import { FOCUS_RING } from '../components/focusRing.ts'

/**
 * Simulating an verification without the ERP.
 *
 * Three steps, in this order for a reason. Upload a prescription; watch the
 * system read it, saying which half it is on; then edit the quotation the
 * reading implies and decide whether to dispatch it.
 *
 * **The edit is the point.** Dispatching unedited should produce an verification with
 * no discrepancies, which is the closest thing to an end-to-end assertion that
 * the comparison invents nothing. Changing one dose is how a specific
 * disagreement gets constructed in a second rather than waited for. A version
 * of this dispatched straight from the file, and it could exercise the
 * pipeline but never a chosen divergence.
 *
 * **The wait is named.** Reading is a minute or two of model work, and it once
 * happened inside the upload request behind a word that never changed. It now
 * runs as its own workflow, reporting whether it is locating text or reading
 * it, with the page beside the form the whole time.
 */

export function Simulations({
  onOpenVerification,
  onOpenSimulation,
  onBack,
}: {
  onOpenVerification: (id: string) => void
  onOpenSimulation: (uploadId: string) => void
  onBack: () => void
}) {
  const [file, setFile] = useState<File | undefined>()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [past, setPast] = useState<SimulationRow[]>([])
  const [drafts, setDrafts] = useState<StoredDraft[]>([])

  useEffect(() => {
    void api.simulations().then((rows) => setPast(rows ?? []))
    setDrafts(listDrafts())
  }, [])

  function forget(uploadId: string) {
    forgetDraft(uploadId)
    setDrafts(listDrafts())
  }

  async function upload() {
    if (file === undefined) return
    setBusy(true)
    setError(undefined)
    try {
      const { id } = await api.uploadSimulation(file)
      // Straight to its own page. The upload id is the address, so the edit
      // survives a reload and can be come back to.
      onOpenSimulation(id)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao enviar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Simulação</h1>
        <span className="bg-fill-brand-weak text-text-brand rounded-base text-tTiny px-2 py-0.5 font-semibold">
          interno
        </span>
      </div>

      <p className="text-tP text-text-weak">
        Envie uma receita, ajuste o orçamento que o sistema deduziu dela e dispare. A conferência
        roda de verdade — só o ERP é substituído. Estas execuções não aparecem para mais ninguém.
      </p>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      <div className="rounded-base border-stroke-weak bg-background-raised flex flex-wrap items-center gap-3 border p-4">
        <input
          type="file"
          accept="image/*"
          disabled={busy}
          aria-label="Receita"
          className="text-tP text-text-strong"
          onChange={(e) => {
            setFile(e.target.files?.[0])
            setError(undefined)
          }}
        />
        <Button
          variant="primary"
          tone="brand"
          size="medium"
          disabled={busy || file === undefined}
          onClick={upload}
        >
          {busy ? 'Enviando…' : 'Enviar receita'}
        </Button>
      </div>

      <Drafts drafts={drafts} onOpen={onOpenSimulation} onForget={forget} />
      <PastRuns runs={past} onOpen={onOpenVerification} />
    </div>
  )
}

/**
 * Simulations this browser is still holding an edit for.
 *
 * Local to the machine, so this is not a shared list — it is the answer to
 * "where was I", which is otherwise lost to a closed tab a few minutes into
 * building a divergence.
 */
function Drafts({
  drafts,
  onOpen,
  onForget,
}: {
  drafts: readonly StoredDraft[]
  onOpen: (uploadId: string) => void
  onForget: (uploadId: string) => void
}) {
  if (drafts.length === 0) return null

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-tH6 text-text-strong">Rascunhos neste navegador</h2>
      <ul className="flex flex-col gap-2">
        {drafts.map((draft) => (
          <li
            key={draft.uploadId}
            className="rounded-base border-stroke-weak bg-background-raised flex flex-wrap items-center gap-3 border p-3"
          >
            <span className="text-tP text-text-strong">
              {draft.quotation.preparations.length} preparação(ões)
            </span>
            <span className="text-tSmall text-text-weak">
              editado em {new Date(draft.updatedAt).toLocaleString('pt-BR')}
            </span>
            <span className="grow" />
            <Button
              variant="secondary"
              tone="brand"
              size="small"
              onClick={() => onOpen(draft.uploadId)}
            >
              Continuar
            </Button>
            <Button
              variant="tertiary"
              tone="neutral"
              size="small"
              onClick={() => onForget(draft.uploadId)}
            >
              Descartar
            </Button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function PastRuns({ runs, onOpen }: { runs: SimulationRow[]; onOpen: (id: string) => void }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-tH6 text-text-strong">Simulações anteriores</h2>
      {runs.length === 0 ? (
        <p className="text-tP text-text-weak">Nenhuma ainda.</p>
      ) : (
        <ul className="rounded-base border-stroke-weak bg-background-raised divide-stroke-weak divide-y border">
          {runs.map((run) => (
            <li key={run.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <button
                type="button"
                className={`text-text-brand rounded-base font-semibold ${FOCUS_RING}`}
                onClick={() => onOpen(run.id)}
              >
                {run.source_quotation_number !== null
                  ? `Cópia de ${run.source_branch_code}/${run.source_quotation_number}`
                  : (run.filename ?? `Simulação ${run.quotation_number}`)}
              </button>
              <span className="text-tSmall text-text-weak">{label(STATUS_LABEL, run.status)}</span>
              <span className="text-tSmall text-text-weak ml-auto">
                {new Date(run.created_at).toLocaleString('pt-BR')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
